import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { merge, Subscription } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { Region } from 'src/app/shared/models/dtos/region/region';
import { RoutesKeys } from 'src/app/utils/routes';

type TEditAccountFormGroup = FormGroup<{
  profilePicture: FormControl<string | null>;
  region: FormControl<Region | null>;
}>;

@Component({
  selector: 'app-edit-account',
  templateUrl: './edit-account.component.html',
  styleUrls: ['./edit-account.component.scss'],
})
/**
 * Edit account form
 */
export class EditAccountComponent implements OnInit, OnDestroy {
  @Input() availableRegions?: Region[];
  editAccountFormGroup!: TEditAccountFormGroup;
  loading: boolean = false;
  private readonly subsink = new Subscription();

  /**
   * @costructor
   */
  constructor(
    private readonly userService: UserService,
    private readonly snackBarService: SnackBarService,
    private readonly translationService: TranslocoService,
    private readonly router: Router,
    private readonly authenticatedUserService: AuthenticatedUserService
  ) {}

  /**
   * Sets up edit account form group
   */
  ngOnInit() {
    this.editAccountFormGroup = new FormGroup({
      profilePicture: new FormControl<string | null>(null),
      region: new FormControl<Region | null>(null),
    });

    this.subsink.add(
      this.authenticatedUserService.admin$.subscribe(() => {
        this.editAccountFormGroup
          .get('profilePicture')
          ?.setValue(this.admin?.picture ?? null);
      })
    );

    this.subsink.add(
      this.authenticatedUserService.currentRegion$.subscribe((region) => {
        this.regionFormControl.setValue(region ?? null);
      })
    );
  }

  /**
   * unsubscribe
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Authenticated admin
   */
  get admin() {
    return this.authenticatedUserService.admin;
  }

  /**
   * Current region of authenticated admin
   */
  get currentRegion() {
    return this.authenticatedUserService.currentRegion;
  }

  /**
   * @param {Region} region region to extract name from
   * @return {string} extracted region name
   */
  regionName(region: Region): string {
    return region.name;
  }

  /**
   * A shortcut for region form control
   */
  get regionFormControl() {
    return this.editAccountFormGroup.controls['region'];
  }

  /**
   * A shortcut for profile picture form control
   */
  get profilePictureFormControl() {
    return this.editAccountFormGroup.controls['profilePicture'];
  }

  /**
   * Only enable save account changes if something has changed
   * @return {boolean}
   */
  isSaveAccountChangesDisabled(): boolean {
    return !this.hasProfilePictureChanged() && !this.hasRegionChanged();
  }

  /**
   * Updates region and/or profile picture if they changed
   */
  async saveAccountChanges() {
    this.loading = true;
    const requests = [];
    const selectedRegionId = this.regionFormControl.value?.id;

    if (
      this.hasRegionChanged() &&
      selectedRegionId &&
      selectedRegionId !== undefined
    ) {
      this.authenticatedUserService.currentRegion = this.availableRegions?.find(
        (region) => region.id === selectedRegionId
      );
    }
    const selectedPicture = this.profilePictureFormControl.value;
    if (this.hasProfilePictureChanged()) {
      requests.push(this.userService.updateAdminPicture(selectedPicture!));
    }
    this.subsink.add(
      merge(...requests).subscribe({
        error: () => {
          this.loading = false;
          this.snackBarService.error(
            this.translationService.translate('error')
          );
        },
        complete: () => {
          this.loading = false;
          this.snackBarService.success(
            this.translationService.translate('success')
          );
          if (!this.admin?.regions) {
            this.router.navigate([RoutesKeys.HOME]);
          }
          this.admin!.picture = this.profilePictureFormControl.value!;
        },
      })
    );
  }

  /**
   * Whether the selected region is different from sysadmin's current region
   * @return {boolean}
   */
  private hasRegionChanged(): boolean {
    const newRegionData = JSON.stringify(this.regionFormControl.value);
    const previousRegionData = JSON.stringify(this.admin!.regions);

    return newRegionData !== previousRegionData;
  }

  /**
   * Whether the selected profile picture is different from sysadmin's current
   * profile picture
   * @return {boolean}
   */
  private hasProfilePictureChanged(): boolean {
    return this.admin?.picture !== this.profilePictureFormControl.value;
  }
}
